{
  "title": "How to Review Competition Creation Requests",
  "description": "Review a user's competition creation request and decide to approve it, send it back for changes, or reject it.",
  "headings": [
    "What You'll Accomplish",
    "Applies To",
    "Before You Start",
    "Steps",
    "What You'll See",
    "FAQ"
  ],
  "summary": "When you're done, the request moves to the corresponding review outcome; if approved, the system automatically creates the competition and makes the applicant its first admin.",
  "roleNote": "Platform Administrator",
  "prerequisites": ["You hold the competition-request review capability"],
  "pathLabel": "Platform Administration → Competition Creation Requests",
  "steps": [
    {
      "title": "1. Open the pending requests list",
      "description": "The list is organized by status tab, letting you view requests that are Submitted, Under Review, or Changes Requested."
    },
    {
      "title": "2. View a request's detail",
      "description": "Open a single request to see its full content: competition name, URL slug, hosting organization, contact person, description, timezone, expected timeline and participant count, whether judging is required, whether it's a two-round format, and whether it will collect data from minor students."
    },
    {
      "title": "3. Mark it under review (optional)",
      "description": "You can mark a request \"Under Review\" — this is purely a status marker to help your team coordinate on who's handling it, and doesn't affect whether it can later be approved or rejected."
    },
    {
      "title": "4a. Approve the request",
      "description": "Once everything checks out, click \"Approve\" and enter a reason (required); the system automatically creates the competition (with baseline settings and a first round) and makes the applicant its `competition_admin`."
    },
    {
      "title": "4b. Send back for changes",
      "description": "If information is incomplete or needs clarification, click \"Request Changes\" and enter a reason (required); the applicant sees your reason and can revise and resubmit."
    },
    {
      "title": "4c. Reject the request",
      "description": "If you've decided not to approve it, click \"Reject\" and enter a reason (required)."
    }
  ],
  "afterward": [
    "Once approved, the system creates the competition automatically and the applicant can immediately sign in as its organizer admin",
    "After Request Changes or Reject, the applicant can see the reason you entered",
    "Every review decision requires a reason and is recorded in the platform audit log"
  ],
  "nextSteps": "Once approved, you can point the applicant to manuals like \"How to Create Rounds and Configure Their Schedule\" to finish setting up.",
  "faq": [
    {
      "q": "Do I have to mark a request \"Under Review\" before I can approve or reject it?",
      "a": "No. Requests in any of the three states — Submitted, Under Review, or Changes Requested — can be approved or rejected directly; \"Under Review\" is just a marker to help your team track progress, not a required step before approving or rejecting."
    },
    {
      "q": "Do I need to create the competition myself after approving?",
      "a": "No. Approval triggers the automated creation flow directly — the system creates the competition (with baseline settings and a first round) and makes the applicant its first admin. You don't take any separate creation step."
    },
    {
      "q": "Is it safe to call approve again on an already-approved request?",
      "a": "Yes, it's safe to call approve again. If the request is already approved and its competition already exists, the system simply returns the existing creation result rather than creating a second competition or throwing an error."
    }
  ]
}
